import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Card, EmptyState, Btn, InfoTip, BALANCE_ENTRY_TIP } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtDate, localISO, nameOf, ACCOUNT_TYPE_LABEL, UNKNOWN_ACCOUNT_TYPE, money } from "@/lib/format";
import { MoneyInput } from "@/lib/accountingFormat";
import { psLabel, fundLast4, usePaymentSources } from "@/lib/payments";
import { Bank, Plus, Trash, Wallet, ChartLineUp, Warning, CreditCard, Archive, Star, PencilSimple, CaretDown, Users, ArrowsLeftRight } from "@phosphor-icons/react";
import { ViewButton, RecordView } from "@/components/RecordView";
import { accountFields, payerFields, planFields, RECORD_TITLES } from "@/lib/recordFields";
import { CurrencySelect } from "@/components/CurrencySelect";
import { UnifiedDateInput } from "@/components/UnifiedDateInput";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";
import { DecisionDialog } from "@/components/DecisionDialog";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { NotesField } from "@/components/NotesField";

// Every sentence ZEN-65 puts on screen, in one place, so a wording ruling is a
// one-line change here.
const CP_WORDS = {
  correct: "Correct this balance",
  remove: "Delete this balance",
  correctTitle: (provider) => `Correct balance — ${provider}`,
  correctIntro: "Fix a balance or date you typed. Every total that uses this balance updates as soon as you save.",
  corrected: "Balance corrected",
  deleteTitle: "Delete this balance?",
  removes: (c) => <>Removes the <b>{money(c.balance, "USD")}</b> balance you entered for <b>{fmtDate(c.effective_date)}</b>.</>,
  fallsBack: (n) => <> The estimate will start from your next most recent balance instead: <b>{money(n.balance, "USD")}</b> on <b>{fmtDate(n.effective_date)}</b>.</>,
  stillLatest: (l) => <> Your latest balance (<b>{money(l.balance, "USD")}</b> on <b>{fmtDate(l.effective_date)}</b>) still sets the estimate.</>,
  onlyOne: "This is the only balance on this account. After you delete it, the account shows no balance until you add one, and 529 Projections leaves this account out.",
  deleted: "Balance deleted",
  earlierYear: (y) => `This balance is dated in ${y}. The 529 balance on your ${y} tax packet will change the next time you download it.`,
  fromDistribution: (d) => d
    ? <>From the 529 distribution of {money(d.amount, "USD")} on {fmtDate(d.date)}. To change it, edit that distribution on <Link to="/withdrawals" className="font-semibold text-[#034AA2] underline">529 Distributions</Link>.</>
    : <>From a 529 distribution. To change it, edit that distribution on <Link to="/withdrawals" className="font-semibold text-[#034AA2] underline">529 Distributions</Link>.</>,
  showAll: (n) => `Show all ${n} balances`,
  showFewer: "Show fewer",
};
const SHOWN_AT_FIRST = 4;
// The earliest year among the dates given, when it is before this calendar
// year; otherwise null. Drives the earlier-year note.
const earlierYear = (...dates) => {
  const ys = dates.filter(Boolean).map((d) => parseInt(String(d).slice(0, 4), 10)).filter((y) => y < new Date().getFullYear());
  return ys.length ? Math.min(...ys) : null;
};

export default function Accounts() {
  const qc = useQueryClient();
  const { selectedStudentId } = useAuth();
  const qs = selectedStudentId ? `?student_id=${selectedStudentId}` : "";
  const { data: students = [] } = useQuery({ queryKey: ["students"], queryFn: async () => (await api.get("/students")).data });
  const { data: funds = [] } = useQuery({ queryKey: ["all-funds", selectedStudentId], queryFn: async () => (await api.get(`/fund-accounts${qs}`)).data });
  const { data: accts = [] } = useQuery({ queryKey: ["accounts-cards", selectedStudentId], queryFn: async () => (await api.get(`/accounts-cards${qs}`)).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: spendAgg } = useQuery({ queryKey: ["acct-spend", selectedStudentId], queryFn: async () => (await api.get(`/accounts-cards/summary${qs}`)).data });
  const [fundDlg, setFundDlg] = useState(false); const [acctDlg, setAcctDlg] = useState(false);
  const [editAcct, setEditAcct] = useState(null); const [editFund, setEditFund] = useState(null); const [cpFor, setCpFor] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [fundOpen, setFundOpen] = useState(true);
  const [acctOpen, setAcctOpen] = useState(true);
  const [peopleOpen, setPeopleOpen] = useState(true);
  const [partyDlg, setPartyDlg] = useState(false); const [editParty, setEditParty] = useState(null);
  // The record behind the eye on any of the three lists here — read only.
  // { kind: "account" | "payer" | "plan", record }. (ZEN-702)
  const [viewing, setViewing] = useState(null);
  const [mergeDlg, setMergeDlg] = useState(false);
  const { data: parties } = useQuery({ queryKey: ["parties"], queryFn: async () => (await api.get("/parties")).data });
  const refresh = () => { qc.invalidateQueries(); };
  const states = settings?.us_states || [];
  const sName = (id) => students.find((s) => s.id === id)?.full_name || "—";

  const active = accts.filter((a) => !a.archived);
  const archived = accts.filter((a) => a.archived);
  // The table moved to lib/format.js and is pinned to the server's
  // models.ACCOUNT_TYPES. Both readings below used to print the stored word
  // when it was not in the table. (ZEN-635)
  const acctType = (a) => nameOf(ACCOUNT_TYPE_LABEL, a.type, UNKNOWN_ACCOUNT_TYPE);
  const acctOwnerName = (a) => {
    if (a.owner_type === "student") return students.find(s => s.id === a.student_id)?.full_name || "";
    return parties?.owner?.full_name || parties?.owner?.nickname || "";
  };
  const acctLabel = (a) => psLabel(a, acctOwnerName(a)) || a.nickname || "Account";

  return (
    <div>
      <PageHeader title="Accounts & Payers" subtitle="529 accounts, bank accounts, cards, and people who pay or reimburse." />

      {/* 529 Fund Accounts */}
      <div className="mb-3 flex items-center justify-between">
        <button onClick={() => setFundOpen(!fundOpen)} className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#6b7280] hover:text-[#1f2937]" data-testid="toggle-529">
          <CaretDown size={14} weight="bold" className={`transition-transform ${fundOpen ? "" : "-rotate-90"}`} /> 529 accounts
        </button>
        <Btn onClick={() => { setEditFund(null); setFundDlg(true); }} data-testid="add-fund-btn"><Plus size={16} weight="bold" /> Add 529 account</Btn>
      </div>
      {fundOpen && (
        funds.length === 0 ? <EmptyState icon={Bank} title="No 529 accounts" subtitle="Add a 529 account and a balance checkpoint to track estimated balance." /> : (
          <div className="grid grid-cols-1 gap-4 @lg:grid-cols-2">
            {funds.map((fa) => <FundCard key={fa.id} fa={fa} sName={sName} parties={parties} onCheckpoint={(cp) => setCpFor({ fa, editing: cp || null })} onView={() => setViewing({ kind: "plan", record: fa })} onEdit={() => { setEditFund(fa); setFundDlg(true); }} onChange={refresh} />)}
          </div>
        )
      )}

      {/* Accounts & Cards */}
      <div className="mb-3 mt-8 flex items-center justify-between">
        <button onClick={() => setAcctOpen(!acctOpen)} className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#6b7280] hover:text-[#1f2937]" data-testid="toggle-accts">
          <CaretDown size={14} weight="bold" className={`transition-transform ${acctOpen ? "" : "-rotate-90"}`} /> Other Accounts
        </button>
        <Btn variant="green" onClick={() => { setEditAcct(null); setAcctDlg(true); }} data-testid="add-acct-btn"><Plus size={16} weight="bold" /> Add account</Btn>
      </div>
      {acctOpen && (<>
      {active.length === 0 ? <EmptyState icon={Wallet} title="No accounts" subtitle="Add bank accounts, credit cards, or other financial instruments." /> : (
        <div className="grid grid-cols-1 gap-3 @sm:grid-cols-2 @xl:grid-cols-3">
          {active.map((a) => {
            const spend = spendAgg?.[a.id];
            return (
              <Card key={a.id} className="relative" data-testid={`acct-${a.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-bold">{acctLabel(a)}</div>
                    <div className="text-xs text-[#6b7280]">
                      {acctType(a)} · {a.currency}
                      {a.institution ? ` · ${a.institution}` : ""}
                    </div>
                    <div className="text-xs text-[#6b7280] capitalize">{a.owner_type}{a.owner_type === "student" ? ` · ${sName(a.student_id)}` : ""}</div>
                    {spend && <div className="mt-1 text-xs text-[#6b7280]">{spend.count} expense{spend.count === 1 ? "" : "s"} · {money(spend.total_usd, "USD")}</div>}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {a.is_default && <span className="text-[#D97706]"><Star size={16} weight="fill" /></span>}
                    {/* The eye, immediately before the pencil. (ZEN-702) */}
                    <ViewButton variant="bare" onClick={() => setViewing({ kind: "account", record: a })} testid={`acct-view-${a.id}`} />
                    <button onClick={() => { setEditAcct(a); setAcctDlg(true); }} className="text-[#034AA2]" data-testid={`acct-edit-${a.id}`}><PencilSimple size={16} weight="bold" /></button>
                    <button onClick={async () => { await api.patch(`/accounts-cards/${a.id}/archive`, { archived: true }); toast.success("Archived"); refresh(); }} className="text-[#6b7280]" data-testid={`acct-archive-${a.id}`}><Archive size={16} weight="bold" /></button>
                    <DeleteAccountBtn acct={a} refCount={spend?.count ?? 0} label={acctLabel(a)} onDeleted={refresh} />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {archived.length > 0 && (
        <div className="mt-4">
          <button onClick={() => setShowArchived(!showArchived)} className="text-xs font-semibold text-[#6b7280] hover:underline">{showArchived ? "Hide" : "Show"} {archived.length} archived</button>
          {showArchived && (
            <div className="mt-2 grid grid-cols-1 gap-3 @sm:grid-cols-2 @xl:grid-cols-3 opacity-60">
              {archived.map((a) => (
                <Card key={a.id} data-testid={`acct-${a.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold">{acctLabel(a)}</div>
                      <div className="text-xs text-[#6b7280]">{acctType(a)} · Archived</div>
                    </div>
                    <button onClick={async () => { await api.patch(`/accounts-cards/${a.id}/archive`, { archived: false }); toast.success("Restored"); refresh(); }} className="text-xs font-semibold text-[#034AA2] hover:underline">Restore</button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
      </>)}

      {/* People / Payers */}
      <div className="mb-3 mt-8 flex items-center justify-between">
        <button onClick={() => setPeopleOpen(!peopleOpen)} className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide text-[#6b7280] hover:text-[#1f2937]" data-testid="toggle-people">
          <CaretDown size={14} weight="bold" className={`transition-transform ${peopleOpen ? "" : "-rotate-90"}`} /> Payers
        </button>
        <div className="flex gap-2">
          {(parties?.custom || []).length >= 2 && (
            <Btn variant="ghost" onClick={() => setMergeDlg(true)} data-testid="merge-people-btn"><ArrowsLeftRight size={16} weight="bold" /> Merge</Btn>
          )}
          <Btn variant="green" onClick={() => { setEditParty(null); setPartyDlg(true); }} data-testid="add-person-btn"><Plus size={16} weight="bold" /> Add person</Btn>
        </div>
      </div>
      {peopleOpen && (
        (parties?.custom || []).length === 0
          ? <EmptyState icon={Users} title="No payers added" subtitle="Payers — people who pay or reimburse (e.g., Grandpa) — are added here or inline when you record an expense." subtitleClassName="mt-1 max-w-lg md:max-w-none md:whitespace-nowrap text-sm text-[#6b7280]" />
          : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {(parties?.custom || []).map((p) => (
                <Card key={p.id} data-testid={`person-${p.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="font-bold truncate">{p.nickname}</div>
                      {p.full_name && p.full_name !== p.nickname && <div className="text-xs text-[#6b7280] truncate">{p.full_name}</div>}
                      {p.role && p.role !== "other" && <div className="text-xs text-[#6b7280] capitalize">{p.role}</div>}
                      <div className="mt-1 text-xs text-[#6b7280]">Used in {p.ref_count ?? 0} record{(p.ref_count ?? 0) !== 1 ? "s" : ""}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {/* The eye, immediately before the pencil. (ZEN-702) */}
                      <ViewButton variant="bare" className="rounded p-1 hover:bg-[#E9E9EB]" onClick={() => setViewing({ kind: "payer", record: p })} testid={`view-person-${p.id}`} />
                      <button onClick={() => { setEditParty(p); setPartyDlg(true); }} className="rounded p-1 hover:bg-[#E9E9EB]" data-testid={`edit-person-${p.id}`}><PencilSimple size={16} weight="bold" className="text-[#6b7280]" /></button>
                      <DeletePersonBtn party={p} onDeleted={refresh} />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )
      )}

      <FundDialog open={fundDlg} onClose={() => { setFundDlg(false); setEditFund(null); }} editing={editFund} students={students} states={states} parties={parties} onSaved={refresh} />
      <AccountCardDialog open={acctDlg} onClose={() => { setAcctDlg(false); setEditAcct(null); }} editing={editAcct} students={students} parties={parties} onSaved={refresh} />
      {cpFor && <CheckpointDialog fa={cpFor.fa} editing={cpFor.editing} onClose={() => setCpFor(null)} onSaved={refresh} />}
      <PersonDialog open={partyDlg} onClose={() => { setPartyDlg(false); setEditParty(null); }} editing={editParty} onSaved={refresh} />
      <MergeDialog open={mergeDlg} onClose={() => setMergeDlg(false)} parties={parties?.custom || []} onMerged={refresh} />
      {/* The field style, once for every window on this page (ZEN-772): when each
          window loaded its own, Add person and Merge forgot to and drew no borders. */}
      <Style />
      {/* ONE panel for the three lists on this screen; which record it draws,
          and which form its Edit opens, follows the kind. (ZEN-702) */}
      <RecordView
        open={!!viewing}
        onClose={() => setViewing(null)}
        title={viewing ? RECORD_TITLES[viewing.kind] : ""}
        fields={!viewing ? [] : viewing.kind === "account"
          ? accountFields(viewing.record, { label: acctLabel(viewing.record), ownerName: parties?.owner?.full_name || parties?.owner?.nickname || "", studentName: viewing.record.owner_type === "student" ? sName(viewing.record.student_id) : "" })
          : viewing.kind === "payer"
            ? payerFields(viewing.record)
            : planFields(viewing.record, {
                label: psLabel({ type: "529_plan", state: viewing.record.state, last4: fundLast4(viewing.record) },
                  parties?.owner?.full_name || parties?.owner?.nickname || "", sName(viewing.record.student_id) === "—" ? "" : sName(viewing.record.student_id)),
                studentName: sName(viewing.record.student_id) === "—" ? "" : sName(viewing.record.student_id),
              })}
        testid="account-view"
      />
    </div>
  );
}

export function FundCard({ fa, sName, parties, onCheckpoint, onView, onEdit, onChange }) {
  const { data: bal } = useQuery({ queryKey: ["bal", fa.id], queryFn: async () => (await api.get(`/fund-accounts/${fa.id}/balance`)).data });
  const { data: cps = [] } = useQuery({ queryKey: ["cps", fa.id], queryFn: async () => (await api.get(`/fund-accounts/${fa.id}/checkpoints`)).data });
  const [delOpen, setDelOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [cpDel, setCpDel] = useState(null);
  const [cpBusy, setCpBusy] = useState(false);
  const ownerName = parties?.owner?.full_name || parties?.owner?.nickname || "";
  // sName() returns the em-dash placeholder while the students query is in
  // flight or has failed; passing that through would render "… *5678 —" as
  // though the dash were a beneficiary. No name means no initials.
  const benResolved = sName(fa.student_id);
  const fundLabel = psLabel({ type: "529_plan", state: fa.state, last4: fundLast4(fa) },
    ownerName, benResolved === "—" ? "" : benResolved);
  return (<>
    <Card data-testid={`fund-${fa.id}`} className={fa.archived ? "opacity-60" : ""}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-lg font-extrabold">{fundLabel || fa.provider}</div>
          <div className="text-xs text-[#6b7280]">{sName(fa.student_id)} · {fa.provider}</div>
        </div>
        <div className="flex items-center gap-1.5">
          {/* The eye, immediately before the pencil. (ZEN-702) */}
          <ViewButton variant="bare" onClick={onView} testid={`fund-view-${fa.id}`} />
          <button onClick={onEdit} className="text-[#034AA2]" data-testid={`fund-edit-${fa.id}`}><PencilSimple size={16} weight="bold" /></button>
          {/* Edit / archive / delete — the same three options as every other
              account (owner ruling, 2026-08-09). 529 cards had only edit and
              delete, so a closed or rolled-over plan could not be set aside. */}
          <button onClick={async () => { await api.patch(`/fund-accounts/${fa.id}/archive`, { archived: !fa.archived }); toast.success(fa.archived ? "Restored" : "Archived"); onChange(); }} className="text-[#6b7280]" data-testid={`fund-archive-${fa.id}`} aria-label={fa.archived ? "Restore" : "Archive"}><Archive size={16} weight="bold" /></button>
          <button onClick={() => setDelOpen(true)} className="text-[#F01919]" data-testid={`fund-delete-${fa.id}`}><Trash size={16} weight="bold" /></button>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-[#C2C9D1] p-3">
          <div className="text-[11px] font-bold uppercase text-[#6b7280]">Latest actual</div>
          <div className="text-xl font-extrabold tnum">{bal?.latest_actual != null ? money(bal.latest_actual, "USD") : "—"}</div>
          <div className="text-[11px] text-[#6b7280]">{bal?.latest_date ? fmtDate(bal.latest_date) : "no checkpoint"}</div>
        </div>
        <div className="rounded-lg border border-[#034AA2] bg-[#034AA2]/5 p-3">
          <div className="text-[11px] font-bold uppercase text-[#034AA2]">Estimated now</div>
          <div className="text-xl font-extrabold tnum text-[#034AA2]">{bal?.anchored ? money(bal.estimated, "USD") : "—"}</div>
          {/* "withdrawn" counts only what left since the anchor. With no anchor
              there is nothing to count from, and the old copy read "withdrawn
              $0.00" on accounts that had real withdrawals. Say what is actually
              needed instead. */}
          <div className="text-[11px] text-[#6b7280]">
            {bal?.anchored ? `excl. growth · withdrawn ${money(bal.withdrawn_since || 0, "USD")}` : "Add a balance to estimate"}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-[#6b7280]">{cps.length} checkpoint(s)</span>
        <Btn variant="ghost" className="h-8" onClick={() => onCheckpoint()} data-testid={`add-cp-${fa.id}`}><ChartLineUp size={14} weight="bold" /> Add / re-anchor balance</Btn>
      </div>
      {cps.length > 0 && (
        <div className="mt-2 space-y-1">
          {(showAll ? cps : cps.slice(0, SHOWN_AT_FIRST)).map((c) => (
            <div key={c.id} className="rounded bg-[#E9E9EB] px-2 py-1 text-xs" data-testid={`cp-row-${c.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 break-words"><span className="whitespace-nowrap">{fmtDate(c.effective_date)}</span>{c.note ? ` · ${c.note}` : ""}</span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="tnum font-semibold">{money(c.balance, "USD")}{c.market_change != null ? <em className={`ml-1 not-italic ${c.market_change >= 0 ? "text-[#077A39]" : "text-[#F01919]"}`}>({c.market_change >= 0 ? "+" : ""}{money(c.market_change, "USD")} mkt)</em> : ""}</span>
                  {/* Only a server that knows ZEN-65 says `editable`; until it is
                      live the row stays as it was rather than offering buttons
                      that would fail. */}
                  {c.editable === true && (<>
                    <button onClick={() => onCheckpoint(c)} className="text-[#034AA2]" title={CP_WORDS.correct} aria-label={CP_WORDS.correct} data-testid={`cp-edit-${c.id}`}><PencilSimple size={14} weight="bold" /></button>
                    <button onClick={() => setCpDel(c)} className="text-[#F01919]" title={CP_WORDS.remove} aria-label={CP_WORDS.remove} data-testid={`cp-delete-${c.id}`}><Trash size={14} weight="bold" /></button>
                  </>)}
                </span>
              </div>
              {"distribution" in c && (
                <div className="mt-0.5 text-[11px] text-[#6b7280]" data-testid={`cp-from-distribution-${c.id}`}>{CP_WORDS.fromDistribution(c.distribution)}</div>
              )}
            </div>
          ))}
          {cps.length > SHOWN_AT_FIRST && (
            <button onClick={() => setShowAll((v) => !v)} className="text-xs font-semibold text-[#034AA2]" data-testid={`cp-show-all-${fa.id}`}>
              {showAll ? CP_WORDS.showFewer : CP_WORDS.showAll(cps.length)}
            </button>
          )}
        </div>
      )}
    </Card>
    <DeleteConfirmDialog open={!!cpDel} onClose={() => setCpDel(null)} busy={cpBusy}
      onConfirm={async () => {
        if (cpBusy) return;
        setCpBusy(true);
        try { await api.delete(`/fund-accounts/${fa.id}/checkpoints/${cpDel.id}`); toast.success(CP_WORDS.deleted); setCpDel(null); onChange(); }
        catch (e) { toast.error(apiErr(e)); }
        finally { setCpBusy(false); }
      }}
      title={CP_WORDS.deleteTitle}
      description={cpDel && <CheckpointDeleteWords cp={cpDel} cps={cps} />} />
    <DeleteConfirmDialog open={delOpen} onClose={() => setDelOpen(false)}
      onConfirm={async () => {
        try { await api.delete(`/fund-accounts/${fa.id}`); toast.success("Deleted"); onChange(); setDelOpen(false); }
        catch (e) { toast.error(apiErr(e)); }
      }}
      title="Delete 529 account?"
      description={<>Removes <b>{fa.provider}</b> from your lists. Distributions and contributions recorded against it keep their history — if any exist, archive it instead.</>} />
  </>);
}


// What the delete box says: which balance goes, and what the estimate starts
// from afterwards. `cps` arrives in the server's anchoring order, so its first
// row is the one the estimate starts from today.
function CheckpointDeleteWords({ cp, cps }) {
  const rest = cps.filter((c) => c.id !== cp.id);
  const year = earlierYear(cp.effective_date);
  return (<>
    {rest.length === 0 ? CP_WORDS.onlyOne
      : <>{CP_WORDS.removes(cp)}{cps[0].id === cp.id ? CP_WORDS.fallsBack(rest[0]) : CP_WORDS.stillLatest(cps[0])}</>}
    {year && <p className="mt-2 text-xs text-[#6b7280]" data-testid="cp-earlier-year">{CP_WORDS.earlierYear(year)}</p>}
  </>);
}


export function CheckpointDialog({ fa, editing, onClose, onSaved }) {
  const [f, setF] = useState(editing
    ? { balance: String(editing.balance), effective_date: editing.effective_date, note: editing.note || "" }
    : { balance: "", effective_date: localISO(), note: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (saving) return;
    setSaving(true);
    const body = { ...f, balance: parseFloat(f.balance) || 0 };
    try {
      if (editing) { await api.put(`/fund-accounts/${fa.id}/checkpoints/${editing.id}`, body); toast.success(CP_WORDS.corrected); }
      else { await api.post(`/fund-accounts/${fa.id}/checkpoints`, body); toast.success("Balance anchored"); }
      onSaved(); onClose();
    }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };
  const year = editing && earlierYear(editing.effective_date, f.effective_date);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editing ? CP_WORDS.correctTitle(fa.provider) : <>Anchor balance — {fa.provider}</>}</DialogTitle></DialogHeader>
        <p className="text-xs text-[#6b7280]">{editing ? CP_WORDS.correctIntro : "Enter an actual balance from a statement. The app shows market change vs. its prior estimate and re-baselines."}</p>
        {year && <p className="text-xs text-[#6b7280]" data-testid="cp-earlier-year">{CP_WORDS.earlierYear(year)}</p>}
        <Fld label="Actual balance" info={BALANCE_ENTRY_TIP}>
          <MoneyInput currency="USD" value={f.balance} onChange={(v) => set("balance", v)} testid="cp-balance" />
        </Fld>
        <Fld label="Effective date"><UnifiedDateInput value={f.effective_date} onChange={(v) => set("effective_date", v)} testid="cp-date" /></Fld>
        <NotesField value={f.note} onChange={(v) => set("note", v)} testid="cp-note" />
        <DialogFooter className="gap-2"><Btn variant="ghost" className="flex-1" onClick={onClose}>Cancel</Btn><Btn enterPrimary variant="green" className="flex-1" onClick={save} disabled={saving} data-testid="cp-save">{saving ? "Saving…" : "Save"}</Btn></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function FundDialog({ open, onClose, editing, students, states, parties, onSaved }) {
  const emptyForm = { student_id: "", provider: "", state: "UT", label: "", account_number_last4: "", notes: "" };
  const [f, setF] = useState(emptyForm);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const isEdit = !!editing;
  React.useEffect(() => {
    if (open) {
      if (editing) {
        setF({
          student_id: editing.student_id || "", provider: editing.provider || "",
          state: editing.state || "UT", label: editing.label || "",
          account_number_last4: editing.account_number_last4 || "", notes: editing.notes || "",
        });
      } else {
        const autoStudent = students.length === 1 ? students[0].id : "";
        setF({ ...emptyForm, student_id: autoStudent });
      }
    }
  }, [open, editing]); // eslint-disable-line

  const ownerName = parties?.owner?.full_name || parties?.owner?.nickname || "";
  // The preview shows the label exactly as it will render, beneficiary initials
  // included — they appear as soon as a beneficiary is chosen in the dialog.
  const benName = students.find((s) => s.id === f.student_id)?.full_name || "";
  const autoLabel = psLabel({ type: "529_plan", state: f.state, last4: fundLast4(f) }, ownerName, benName);

  const save = async () => {
    if (!f.student_id || !f.provider) { toast.error("Beneficiary & provider required"); return; }
    try {
      if (isEdit) await api.put(`/fund-accounts/${editing.id}`, f);
      else await api.post("/fund-accounts", f);
      toast.success(isEdit ? "Updated" : "Added"); onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{isEdit ? "Account" : "Add 529 account"}</DialogTitle></DialogHeader>

        {/* Auto-generated label */}
        <div data-testid="fund-auto-label">
          <div className="mb-0.5 flex items-center gap-1.5 text-xs font-bold uppercase text-[#6b7280]">
            Account label
            <span className="rounded bg-[#E9E9EB] px-1 py-px text-[9px] font-bold uppercase tracking-wider text-[#6b7280]">auto</span>
            <InfoTip testid="fund-label-info" text="This is the short name shown wherever this 529 account appears in the app." />
          </div>
          <div className="flex h-10 items-center rounded-md border border-[#C2C9D1] bg-[#F8FAFC] px-3 text-sm font-semibold text-[#1f2937]" data-testid="fund-label-display">
            {autoLabel || <span className="text-[#9CA3AF]">Fill in the fields below</span>}
          </div>
        </div>

        <Fld label="Beneficiary"><select className="inp" value={f.student_id} onChange={(e) => set("student_id", e.target.value)} data-testid="fund-student"><option value="">Select…</option>{students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}</select></Fld>
        <Fld label="Provider / plan name"><input className="inp" value={f.provider} onChange={(e) => set("provider", e.target.value)} data-testid="fund-provider" placeholder="my529 (Utah)" /></Fld>
        <div className="grid grid-cols-2 gap-3">
          <Fld label="Plan home state"><select className="inp" value={f.state} onChange={(e) => set("state", e.target.value)} data-testid="fund-state">{states.map((s) => <option key={s}>{s}</option>)}</select></Fld>
          <Fld label="Last 4 digits"><input className="inp" value={f.account_number_last4} onChange={(e) => set("account_number_last4", e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="fund-last4" inputMode="numeric" placeholder="0000" /></Fld>
        </div>
        <NotesField value={f.notes} onChange={(v) => set("notes", v)} testid="fund-notes" />
        <DialogFooter className="gap-2"><Btn variant="ghost" onClick={onClose} className="flex-1">Cancel</Btn><Btn enterPrimary variant="green" onClick={save} data-testid="fund-save" className="flex-1">Save</Btn></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
function AccountCardDialog({ open, onClose, editing, students, parties, onSaved }) {
  const TYPES = [
    { value: "checking", label: "Checking" }, { value: "savings", label: "Savings" },
    { value: "credit_card", label: "Credit Card" }, { value: "prepaid", label: "Prepaid" }, { value: "other", label: "Other" },
  ];
  const emptyForm = { institution: "", last4: "", type: "checking", currency: "USD", owner_type: "owner", student_id: "", is_default: false, notes: "" };
  const [f, setF] = useState(emptyForm);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  // EVERY account in the subscription, not the filtered page list — the one
  // being replaced may belong to a person the page is not currently showing.
  //
  // AND FETCHED FRESH EACH TIME THIS OPENS. Query results are treated as good
  // for a minute app-wide, which is right for a screen you are reading and
  // wrong for a question you are being asked: a default someone changed in the
  // last minute would leave this dialog naming the wrong account, or skipping
  // the question altogether because it still believes there is nothing to
  // displace. The server keeps one default per person either way; what a stale
  // read costs is the warning.
  const { data: psData, refetch: refetchAccounts } = usePaymentSources(true);
  const allAccounts = psData?.sources || [];
  React.useEffect(() => { if (open) refetchAccounts(); }, [open]); // eslint-disable-line
  const [replacing, setReplacing] = useState(null);
  const [saving, setSaving] = useState(false);
  React.useEffect(() => {
    if (open) {
      if (editing) {
        setF({
          institution: editing.institution || "", last4: editing.last4 || "",
          type: editing.type || "checking", currency: editing.currency || "USD",
          owner_type: editing.owner_type || "owner", student_id: editing.student_id || "",
          is_default: !!editing.is_default, notes: editing.notes || "",
        });
      } else {
        const autoStudent = students.length === 1 ? students[0].id : "";
        setF({ ...emptyForm, student_id: autoStudent });
      }
    }
  }, [open, editing]); // eslint-disable-line

  // Derive holder name for psLabel
  const holderName = f.owner_type === "student"
    ? (students.find(s => s.id === f.student_id)?.full_name || "")
    : (parties?.owner?.full_name || parties?.owner?.nickname || "");

  // Live auto-generated label
  const autoLabel = psLabel({ institution: f.institution, last4: f.last4, currency: f.currency, type: f.type }, holderName);

  // Accounts that predate the completeness rule exist and are legitimate. If the
  // rule were enforced on every save, such an account could never be edited at
  // all — you could not change its notes or currency without inventing a last 4
  // you may not have. So: new accounts must be complete, and an account that IS
  // complete cannot be made incomplete, but a legacy incomplete one still saves.
  const wasComplete = Boolean(editing)
    && Boolean((editing?.institution || "").trim())
    && (editing?.last4 || "").replace(/\D/g, "").length === 4;
  const mustBeComplete = !editing || wasComplete;

  // A DEFAULT BELONGS TO ONE PERSON, and taking it from them is worth a
  // sentence. (ZEN-52)
  //
  // Before this, marking any account as the default silently cleared the flag
  // on every other account in the subscription — a beneficiary setting hers
  // unset the account owner's, and nobody was told. Defaults are now per
  // person, and replacing one asks first. Setting a person's FIRST default
  // says nothing: there is nothing to displace.
  const sameHolder = (a) => (f.owner_type === "student"
    ? (a.owner_type === "student" && a.student_id === f.student_id)
    : a.owner_type !== "student");
  const defaultHeldBySomeoneElse = (rows) => (rows || []).find((a) =>
    a.is_default && !a.archived && !a.deleted && a.id !== editing?.id && sameHolder(a));
  const currentDefault = defaultHeldBySomeoneElse(allAccounts);
  const holderIsOwner = f.owner_type !== "student";
  const holderStudent = students.find((st) => st.id === f.student_id);
  const holderFirstName = holderStudent?.nickname
    || (holderStudent?.full_name || "").split(" ")[0]
    || "this beneficiary";
  const beingReplaced = replacing?.held || currentDefault;
  const currentDefaultLabel = beingReplaced
    ? (psLabel(beingReplaced, beingReplaced.owner_type === "student"
        ? (students.find((st) => st.id === beingReplaced.student_id)?.full_name || "")
        : (parties?.owner?.full_name || parties?.owner?.nickname || "")) || beingReplaced.nickname || "the account it is now")
    : "";

  const save = async () => {
    // Institution and last 4 are both required. The label is built from holder
    // initials + institution + last 4, and there is one owner per subscription —
    // so with either field blank, two accounts collapse to the same bare
    // initials in every payment picker, where nothing else distinguishes them.
    // Owner ruling 2026-08-09: an account that gets used should be complete.
    const last4 = (f.last4 || "").replace(/\D/g, "").slice(0, 4);
    if (mustBeComplete) {
      if (!(f.institution || "").trim()) { toast.error("Institution is required — it's what tells your accounts apart"); return; }
      if (last4.length !== 4) { toast.error("Enter 4 digits — use 1111 if you'd rather not record the real ones"); return; }
    }
    const body = { ...f, nickname: autoLabel || "Account", last4 };
    // Only a REPLACEMENT asks. Turning the flag off, leaving it as it was, or
    // setting the very first default for this person all save straight through.
    //
    // The test is "does SOMEONE ELSE'S account lose the flag", not "was this
    // account already a default". Those come apart when an account changes
    // hands: an account that is already the ACCOUNT OWNER's default, moved to
    // a beneficiary who has one of their own, displaces theirs — and an
    // earlier version of this check waved it through because the box had been
    // ticked all along. currentDefault is computed against the holder this
    // save is giving the account, and excludes the account being edited, so it
    // is null in exactly the cases that should not ask.
    // (Adversarial review, round 1.)
    // ASKED AGAINST A FRESH ANSWER, NOT A PENDING ONE. The refresh started
    // when this opened is not necessarily finished — press Save straight away,
    // or have a slow connection, and the list this reads can still be empty or
    // a minute old, so the question that is supposed to protect somebody's
    // default is simply never asked. Waiting for the answer here is the only
    // place it matters. (Adversarial review, round 5.)
    if (f.is_default) {
      let rows = allAccounts;
      try {
        const fresh = await refetchAccounts();
        rows = fresh?.data?.sources || rows;
      } catch { /* fall back to what we have — better a late question than none */ }
      const held = defaultHeldBySomeoneElse(rows);
      if (held) { setReplacing({ body, held }); return; }
    }
    await commit(body);
  };

  const commit = async (body) => {
    setSaving(true);
    try {
      if (editing) await api.put(`/accounts-cards/${editing.id}`, body);
      else await api.post("/accounts-cards", body);
      toast.success(editing ? "Updated" : "Added"); setReplacing(null); onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  // Owner label with real name
  const ownerDisplayName = parties?.owner?.full_name || parties?.owner?.nickname || "Owner";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Account</DialogTitle></DialogHeader>

        {/* Nudge, not a block. This account predates the completeness rule, so it
            still saves without both fields — but filling them is what stops two
            accounts sharing the same bare initials in the payment pickers. */}
        {editing && !wasComplete && (
          <div className="rounded-md border border-[#C2C9D1] bg-[#F4F7FB] px-3 py-2 text-xs text-[#374151]" data-testid="acctd-incomplete-note">
            Adding the institution and last 4 digits will tell this account apart from your others everywhere it appears. You can save without them.
          </div>
        )}

        {/* Auto-generated label (read-only) */}
        <div data-testid="acctd-auto-label">
          <div className="mb-0.5 flex items-center gap-1.5 text-xs font-bold uppercase text-[#6b7280]">
            Account label
            <span className="rounded bg-[#E9E9EB] px-1 py-px text-[9px] font-bold uppercase tracking-wider text-[#6b7280]">auto</span>
            <InfoTip testid="acctd-label-info" text="The short name shown wherever this account appears in the app. It's built automatically from the fields below." />
          </div>
          <div className="flex h-10 items-center rounded-md border border-[#C2C9D1] bg-[#F8FAFC] px-3 text-sm font-semibold text-[#1f2937]" data-testid="acctd-nickname">
            {autoLabel || <span className="text-[#9CA3AF]">Fill in the fields below</span>}
          </div>
        </div>

        <Fld label="Account owner"><select className="inp" value={f.owner_type} onChange={(e) => set("owner_type", e.target.value)} data-testid="acctd-owner"><option value="owner">Owner ({ownerDisplayName})</option><option value="student">Beneficiary</option></select></Fld>
        {f.owner_type === "student" && <Fld label="Which beneficiary"><select className="inp" value={f.student_id} onChange={(e) => set("student_id", e.target.value)} data-testid="acctd-student"><option value="">Select…</option>{students.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}</select></Fld>}
        <Fld label={<>Institution · short code <InfoTip testid="acctd-institution-info" text="Use a short code, not the full legal name — it appears in the account label everywhere." /></>}><input className="inp" value={f.institution} onChange={(e) => set("institution", e.target.value)} data-testid="acctd-institution" placeholder="WF, BoA, RBC, TD, Chase" /></Fld>
        <Fld label="Type"><select className="inp" value={f.type} onChange={(e) => set("type", e.target.value)} data-testid="acctd-type">{TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></Fld>
        <div className="grid grid-cols-2 gap-3">
          <Fld label={<>Last 4 digits <InfoTip testid="acctd-last4-info" text="Used to tell your accounts apart — it appears in the account label. If you'd rather not record the real digits, enter 1111 (or any four you'll recognize); nothing checks them against your bank." /></>}><input className="inp" value={f.last4} onChange={(e) => set("last4", e.target.value.replace(/\D/g, "").slice(0, 4))} data-testid="acctd-last4" inputMode="numeric" placeholder="0000" /></Fld>
          <Fld label="Currency"><CurrencySelect value={f.currency} onChange={(v) => set("currency", v)} testid="acctd-currency" /></Fld>
        </div>
        <NotesField value={f.notes} onChange={(v) => set("notes", v)} testid="acctd-notes" />
        {/* Names WHOSE default this is. Each person has their own now, so
            "set as default for expenses" no longer says enough. */}
        <label className="mt-1 flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={f.is_default} onChange={(e) => set("is_default", e.target.checked)} data-testid="acctd-default" /> {holderIsOwner ? "Make this your default account for new expenses" : `Make this ${holderFirstName}'s default account for new expenses`}</label>
        <DialogFooter className="gap-2"><Btn variant="ghost" onClick={onClose} className="flex-1">Cancel</Btn><Btn enterPrimary variant="green" onClick={save} data-testid="acctd-save" className="flex-1">Save</Btn></DialogFooter>
      <DecisionDialog
        open={!!replacing}
        onClose={() => setReplacing(null)}
        onConfirm={() => commit(replacing.body)}
        busy={saving}
        actionLabel="Make it the default"
        testid="acct-default-replace"
        title={holderIsOwner ? "Change your default account?" : `Change ${holderFirstName}'s default account?`}
      >
        <div className="space-y-2 text-sm text-[#374151]">
          <p data-testid="acct-default-replace-body">
            {holderIsOwner
              ? <>Your default is currently <span className="font-semibold">{currentDefaultLabel}</span>. Making <span className="font-semibold">{autoLabel || "this account"}</span> your default means it will be pre-selected on new expenses from now on.</>
              : <>{holderFirstName}&rsquo;s default is currently <span className="font-semibold">{currentDefaultLabel}</span>. Making <span className="font-semibold">{autoLabel || "this account"}</span> their default means it will be pre-selected on new expenses for {holderFirstName} from now on.</>}
          </p>
          <p>Expenses already recorded keep the account they were saved with — this changes nothing for them.</p>
        </div>
      </DecisionDialog>
      </DialogContent>
    </Dialog>
  );
}

function Fld({ label, children, info }) {
  return (
    <label className="block">
      <span className="mb-1 flex items-center gap-1 text-xs font-bold uppercase text-[#6b7280]">
        {label}{info && <InfoTip text={info} testid="fld-info" />}
      </span>
      {children}
    </label>
  );
}
function Style() { return <style>{`.inp{height:40px;width:100%;border:1px solid #C2C9D1;border-radius:6px;padding:0 10px;font-size:14px;background:#fff}`}</style>; }


// Delete is for mistakes — a duplicate, a typo. Archive is for accounts that carry
// real history. So an account any expense points at is never deletable; the dialog
// turns into an explainer instead. Mirrors DeletePersonBtn below.
// refCount comes from the page's spend aggregate, which is filtered (non-draft, and
// scoped to the selected beneficiary/tax year) — so it is a hint, not the authority.
// The backend counts every expense and is the thing that actually refuses.
function DeleteAccountBtn({ acct, refCount, label, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const canDelete = refCount === 0;
  const doDelete = async () => {
    setBusy(true);
    try { await api.delete(`/accounts-cards/${acct.id}`); toast.success("Deleted"); onDeleted(); setOpen(false); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="rounded p-1 text-[#F01919] hover:bg-[#FBE9E9]" data-testid={`acct-delete-${acct.id}`} aria-label={`Delete ${label}`}><Trash size={16} weight="bold" /></button>
      <DeleteConfirmDialog
        open={open} onClose={() => setOpen(false)} onConfirm={canDelete ? doDelete : undefined} busy={busy}
        title={canDelete ? `Delete ${label}?` : `Can't delete ${label}`}
        description={canDelete
          ? "No expenses use this account, so it will be permanently removed."
          : `Used by ${refCount} expense${refCount !== 1 ? "s" : ""}. Archive it instead — that hides it from the payment picker and keeps those records intact.`}
      />
    </>
  );
}


function DeletePersonBtn({ party, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const canDelete = (party.ref_count ?? 0) === 0;
  const doDelete = async () => {
    setBusy(true);
    try { await api.delete(`/parties/${party.id}`); toast.success("Deleted"); onDeleted(); setOpen(false); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="rounded p-1 hover:bg-[#FBE9E9]" data-testid={`del-person-${party.id}`}><Trash size={16} weight="bold" className="text-[#F01919]" /></button>
      <DeleteConfirmDialog
        open={open} onClose={() => setOpen(false)} onConfirm={canDelete ? doDelete : undefined} busy={busy}
        title={canDelete ? `Delete ${party.nickname}?` : `Can't delete ${party.nickname}`}
        description={canDelete
          ? "This person has no records and will be permanently removed."
          : `Referenced in ${party.ref_count} record${party.ref_count !== 1 ? "s" : ""}. Use Merge to move records to another person first.`}
      />
    </>
  );
}


function PersonDialog({ open, onClose, editing, onSaved }) {
  const [f, setF] = useState({ nickname: "", full_name: "", role: "other", email: "", notes: "" });
  React.useEffect(() => {
    if (editing) setF({ nickname: editing.nickname || "", full_name: editing.full_name || "", role: editing.role || "other", email: editing.email || "", notes: editing.notes || "" });
    else setF({ nickname: "", full_name: "", role: "other", email: "", notes: "" });
  }, [editing, open]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.nickname.trim()) { toast.error("Nickname required"); return; }
    try {
      if (editing) await api.put(`/parties/${editing.id}`, f);
      else await api.post("/parties", f);
      toast.success(editing ? "Updated" : "Added"); onSaved(); onClose();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editing ? "Edit person" : "Add person"}</DialogTitle></DialogHeader>
        <Fld label="Nickname *"><input className="inp" value={f.nickname} onChange={(e) => set("nickname", e.target.value)} data-testid="person-nickname" placeholder="e.g., Grandpa" /></Fld>
        <Fld label="Full name"><input className="inp" value={f.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="person-fullname" placeholder="Optional" /></Fld>
        <Fld label="Relationship"><input className="inp" value={f.role} onChange={(e) => set("role", e.target.value)} data-testid="person-role" placeholder="e.g., Grandfather" /></Fld>
        <Fld label="Email"><input className="inp" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="person-email" placeholder="Optional" /></Fld>
        <NotesField value={f.notes} onChange={(v) => set("notes", v)} testid="person-notes" />
        <DialogFooter className="gap-2">
          <Btn variant="ghost" className="flex-1" onClick={onClose}>Cancel</Btn>
          <Btn enterPrimary variant="green" className="flex-1" onClick={save} data-testid="person-save">Save</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function MergeDialog({ open, onClose, parties, onMerged }) {
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (open) { setSource(""); setTarget(""); setConfirm(false); } }, [open]);
  const srcParty = parties.find((p) => p.id === source);
  const tgtParty = parties.find((p) => p.id === target);
  const canMerge = source && target && source !== target;
  const doMerge = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/parties/${source}/merge-into/${target}`);
      toast.success(`Merged — ${r.data.reassigned} record${r.data.reassigned !== 1 ? "s" : ""} moved`);
      onMerged(); onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Merge duplicate people</DialogTitle></DialogHeader>
        <p className="text-xs text-[#6b7280]">Move all records from one person to another, then delete the source. This can&apos;t be undone.</p>
        <Fld label="Move records FROM">
          <select className="inp" value={source} onChange={(e) => { setSource(e.target.value); setConfirm(false); }} data-testid="merge-source">
            <option value="">— Select —</option>
            {parties.map((p) => <option key={p.id} value={p.id}>{p.nickname}{p.ref_count ? ` (${p.ref_count} records)` : ""}</option>)}
          </select>
        </Fld>
        <Fld label="INTO (keep this person)">
          <select className="inp" value={target} onChange={(e) => { setTarget(e.target.value); setConfirm(false); }} data-testid="merge-target">
            <option value="">— Select —</option>
            {parties.filter((p) => p.id !== source).map((p) => <option key={p.id} value={p.id}>{p.nickname}{p.ref_count ? ` (${p.ref_count} records)` : ""}</option>)}
          </select>
        </Fld>
        {canMerge && !confirm && (
          <div className="rounded-md border border-[#D97706]/30 bg-[#FFFBEB] p-2.5 text-xs text-[#92400E]">
            Move all {srcParty?.ref_count ?? 0} record{(srcParty?.ref_count ?? 0) !== 1 ? "s" : ""} from <b>{srcParty?.nickname}</b> to <b>{tgtParty?.nickname}</b>, then delete <b>{srcParty?.nickname}</b>.
          </div>
        )}
        <DialogFooter>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          {canMerge && !confirm && (
            <Btn className="bg-[#D97706] text-white hover:bg-[#B45309]" onClick={() => setConfirm(true)} data-testid="merge-confirm-step">Confirm merge</Btn>
          )}
          {canMerge && confirm && (
            <Btn className="bg-[#F01919] text-white hover:bg-[#c91010]" onClick={doMerge} disabled={busy} data-testid="merge-execute">
              {busy ? "Merging…" : `Delete ${srcParty?.nickname} and move records`}
            </Btn>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
